import { DOCUMENT, inject } from "@angular/core";
import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, Router } from "@angular/router";
import LoadingService from "@services/loading.service";
import LocalContractService from "@services/local-contract.service";
import LocalContractUserService from "@services/local-contractUser.service";
import LocalUserService from "@services/local-user.service";
import NavService from "@services/nav.service";
import RouteParams from "@shared/route-params";
import { forkJoin, from, Observable, of } from "rxjs";
import { catchError, concatMap, first, map, tap } from "rxjs/operators";
import environment from "src/environments/environment";
import {
  ContractService,
  ContractUserService,
  CorrespondenceItemService,
  DefaultSelection36CorrespondenceTypePayload,
  INavigationRoot,
  IUser,
  NavigationService,
  TPContract,
  TPContractUser,
  UserService,
} from "@api";
import { correspondenceAppId } from "@shared/staticValue";
import { CorrespondenceTypeService } from "@api/api/correspondenceType.service";
import LocalCorrespondenceTypeService from "@services/local-correspondenceType.service";
import { AuthService } from "@bbj/components";
import LocalTaskService from "@services/local-task.service";
import CorrespondenceRole from "@models/CorrespondenceRole";

@Injectable({
  providedIn: "root",
})
export default class LoginUserResolve {
  private loadingService = inject(LoadingService);
  private authService = inject(AuthService);
  router = inject(Router);
  private contractService = inject(ContractService);
  private contractUserService = inject(ContractUserService);
  private userService = inject(UserService);
  private correspondenceService = inject(CorrespondenceItemService);
  private correspondenceTypeService = inject(CorrespondenceTypeService);
  localContractUserService = inject(LocalContractUserService);
  localUserService = inject(LocalUserService);
  localContractService = inject(LocalContractService);
  localCorrespondenceTypeServices = inject(LocalCorrespondenceTypeService);
  localTaskService = inject(LocalTaskService);
  navService = inject(NavService);
  navigationService = inject(NavigationService);
  private document = inject<Document>(DOCUMENT);


  // eslint-disable-next-line sonarjs/cognitive-complexity
  resolve(route: ActivatedRouteSnapshot): Observable<void> {
    this.loadingService.start();
    console.log("Hitting this");
    const contractId = route.params[RouteParams.ContractId];
    if (!contractId) {
      this.document.location.href = environment.url.portal;
    }

    return from(this.authService.login()).pipe(
      tap(()=>this.localTaskService.fetchData()),
      concatMap((user) => {
        if (!user) {
          return of([]);
        }

        const localContract = this.localContractService.getItem();
        const localContractUsers = this.localContractUserService.getItems();
        const localUsers = this.localUserService.getItems();
        const localRole = this.localContractUserService.currentUserContractRole;
        const localCorrType = this.localCorrespondenceTypeServices.getItems();
        const boroughNavigation = this.navService.getBroughData();
        const combinedBoroughs = this.navService.getrecentvalue();
        const globalNavigation = this.navService.getGlobalData();

        if (
          !localContract ||
          !localContractUsers ||
          !localUsers ||
          !localCorrType ||
          !localRole ||
          localContract.Guid !== contractId
        ) {
          return forkJoin([
            this.userService.getAll(),
            this.contractService.getContractById(contractId),
            this.contractUserService.getContractUser(contractId),
            this.correspondenceService.getCorrespondenceRole(contractId),
            this.correspondenceTypeService.getAllCorrespondenceType(),
            this.navigationService.getAllMenus(),
          ]);
        }

        return of([
          localUsers,
          localContract,
          localContractUsers,
          localRole,
          localCorrType,
          { boroughNavigation, combinedBoroughs, globalNavigation },
        ]);
      }),
      map(
        ([users, contract, contractUsers, role, correspondenceType, root]: [
          IUser[],
          TPContract,
          TPContractUser[],
          string,
          DefaultSelection36CorrespondenceTypePayload[],
          INavigationRoot,
        ]) => {
          const loginUser = users.find((u)=>{return u.id===this.authService.getUserId()});
          if (!users || !contract || !loginUser) {
            this.document.location.href = environment.url.portal;
          }
          
          const permission = this.authService.getPermission();
          this.navService.setUserItem(loginUser.givenName, loginUser.surname, loginUser.id, loginUser.mail, loginUser.companyName, permission);
          this.localContractService.currentContract = contract;
          this.localContractUserService.currentUserContractRole = role;

          const doc = contractUsers.find((cu) => {
            return cu.contract_user_application_role.find((a) => {
              return (
                a.ApplicationId === correspondenceAppId &&
                a.ApplicationRoleId === CorrespondenceRole.DocControl
              );
            });
          });

          this.localContractUserService.defaultDoc = doc?.UserId ?? null;
          this.localContractService.setItem(contract);

          this.localContractUserService.setItems(contractUsers);
          this.localUserService.setItems(users);
          this.localCorrespondenceTypeServices.setItems(correspondenceType);

          if (root) {
            this.navService.setBroughData(root.boroughNavigation);
            this.navService.setGlobalData(root.globalNavigation);
            this.navService.setDataSubject(root.combinedBoroughs);

            if (root.globalNavigation.length) {
              this.navService.setSelectedId(
                root.globalNavigation[0].globalModule.Guid,
              );
            } else if (root.boroughNavigation.length) {
              this.navService.setSelectedId(
                root.boroughNavigation[0].boroughItem.Guid,
              );

              this.localContractService.setItem(
                root.boroughNavigation[0].projects[0],
              );
            }
          }
          this.loadingService.stop();
          return;
        },
      ),
      first(),
      catchError((error: HttpErrorResponse) => {
        // console.log(error);
        if (error.status === 404) {
          this.document.location.href = environment.url.portal;
        } else {
          const localContract = this.localContractService.getItem();
          if (localContract?.Guid) {
            this.router.navigate([
              localContract.Guid,
              "error",
              error.status ? error.status : 500,
            ]);
          } else {
            this.router.navigate(["error", error.status ? error.status : 500]);
          }
        }

        return of();
      }),
    );
  }
}
